import { Backdrop, Box, Button, IconButton, Stack } from '@mui/material';
import StopIcon from '@mui/icons-material/Stop';
import { makeStyles } from 'tss-react/mui';
import type { CSSObject } from 'tss-react';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { bindTrigger } from 'material-ui-popup-state/hooks';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { parseReplayTime } from './CameraPlayer.utils';
import { TimelapseSlider } from './TimelapseSlider';
import { CameraPlayerControlCommonProps } from './CameraPlayerControl.types';

const useStyles = makeStyles()((theme) => ({
  root: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    zIndex: 2,
    paddingTop: 'max(env(safe-area-inset-top), 16px)',
    paddingBottom: 'max(env(safe-area-inset-bottom), 16px)',
    paddingLeft: 'max(env(safe-area-inset-left), 16px)',
    paddingRight: 'max(env(safe-area-inset-right), 16px)',
    pointerEvents: 'none',
  },
  container: {
    height: '100%',
    width: '100%',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
  },
  statusContainer: {
    position: 'relative',
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  button: {
    borderRadius: Number(theme.shape.borderRadius) * 2,
    backgroundColor: theme.palette.grey[300],
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.grey[700],
    }),
    pointerEvents: 'auto',
    '&:hover': {
      backgroundColor: theme.palette.grey[500],
    },
    '&:disabled': {
      backgroundColor: theme.palette.grey[700],
    },
  } as CSSObject,
  replayTimeButton: {
    fontWeight: 'bold',
    color: theme.palette.text.primary,
    pointerEvents: 'auto',
    '.MuiButton-endIcon': {
      marginLeft: '2px',
    },
    '&:disabled': {
      color: theme.palette.text.primary,
    },
  },
  loaderContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

export const CameraPlayerMobileFullScreenControls = (
  props: CameraPlayerControlCommonProps,
): JSX.Element => {
  const {
    camera,
    imageTimestamp,
    isLoading,
    playerStatus,
    popupState,
    timelapseFramesLength,
    timelapseFrameNumber,
    timelapseReplayTime,
    handleTogglePlay,
    handleStopTimelapse,
    handleSliderChange,
  } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [showControls, setShowControls] = useState(true);
  const isPlaying = playerStatus === 'playingTimelapse';

  // When the player status transitions from playing to paused because it's at the end of
  // the timelapse, show the controls again.
  useEffect(() => {
    if (playerStatus === 'pausedTimelapse') {
      setShowControls(true);
    }
  }, [playerStatus]);

  const playPauseButton = (
    <IconButton
      className={classes.button}
      onClick={handleTogglePlay}
      disabled={isLoading}
      sx={{
        width: 42,
        height: 42,
        borderRadius: '12px !important',
      }}
      aria-label={
        isPlaying
          ? t('alertCameras.pauseTimelapse')
          : t('alertCameras.playTimelapse')
      }
    >
      {isPlaying ? (
        <PauseIcon
          fontSize="large"
          sx={{ color: 'text.primary', fontSize: 24 }}
        />
      ) : (
        <PlayArrowIcon
          fontSize="large"
          sx={{ color: 'text.primary', fontSize: 24 }}
        />
      )}
    </IconButton>
  );

  const stopButton =
    playerStatus !== 'streamingLive' ? (
      <IconButton
        className={classes.button}
        sx={{
          marginLeft: 1,
        }}
        onClick={handleStopTimelapse}
        disabled={isLoading}
        aria-label={t('alertCameras.stopTimelapse')}
      >
        <StopIcon fontSize="medium" sx={{ color: 'text.primary' }} />
      </IconButton>
    ) : null;

  const timelapseSlider = (
    <Box
      sx={{
        flex: 1,
        minWidth: { xs: '100%', sm: 'unset' },
        paddingBottom: 1.5,
      }}
    >
      <TimelapseSlider
        imageTimestamp={imageTimestamp}
        isLoading={isLoading}
        playerStatus={playerStatus}
        timelapseFrameNumber={timelapseFrameNumber}
        timelapseFramesLength={timelapseFramesLength}
        handleSliderChange={handleSliderChange}
        spacing={1}
        dark
      />
    </Box>
  );

  const replayTimeMenu = (
    <Button
      {...bindTrigger(popupState)}
      disabled={isLoading}
      variant="text"
      size="small"
      endIcon={
        <ExpandMoreIcon fontSize="medium" sx={{ color: 'text.primary' }} />
      }
      className={classes.replayTimeButton}
    >
      {parseReplayTime(timelapseReplayTime, t)}
    </Button>
  );

  const fullScreenToggleButton = (
    <IconButton
      component={Link}
      to={`/camera/${camera.id}`}
      replace
      className={classes.button}
      sx={{
        width: 42,
        height: 42,
        borderRadius: '12px !important',
      }}
      aria-label={t('alertCameras.exitFullScreen')}
    >
      <FullscreenExitIcon fontSize="medium" sx={{ color: 'text.primary' }} />
    </IconButton>
  );

  return (
    <>
      <Backdrop
        open
        onMouseDown={() => setShowControls((prev) => !prev)}
        sx={() => ({
          zIndex: 1,
          backgroundColor: 'transparent',
        })}
      />
      {showControls && (
        <div className={classes.root}>
          <div className={classes.container}>
            <Stack
              direction="row"
              alignItems="end"
              sx={{
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
                position: 'relative',
                zIndex: 2,
                pointerEvents: 'auto',
              }}
            >
              <Box
                sx={{
                  // For screens smaller than 600px (xs breakpoint), hide the play/pause button here
                  // and instead display it below the slider with the timelapse menu and fullscreen button.
                  display: { xs: 'none', sm: 'block' },
                  marginRight: 1,
                }}
              >
                {playPauseButton}
                {stopButton}
              </Box>

              {timelapseSlider}

              <Stack
                direction="row"
                spacing={1}
                sx={{
                  justifyContent: {
                    xs: 'space-between',
                    sm: 'flex-end',
                  },
                  flex: { xs: 1, sm: 'unset' },
                }}
              >
                <Box
                  sx={{
                    // Show the play/pause button here instead for small screens (see above).
                    display: { xs: 'block', sm: 'none' },
                  }}
                >
                  {playPauseButton}
                  {stopButton}
                </Box>

                <Box>
                  {replayTimeMenu}
                  {fullScreenToggleButton}
                </Box>
              </Stack>
            </Stack>
          </div>
        </div>
      )}
    </>
  );
};
